import { supabase } from '../supabase';

/**
 * Deck service — flashcard deck membership over the `thoughts` table.
 *
 * The deck is live DB state, not a generated artifact: membership is a single
 * `in_deck` boolean per thought (NULL=undecided, true=in deck, false=tossed).
 * These helpers only ever read/write `in_deck` — the underlying thought is
 * never modified, archived, or deleted.
 *
 * Plan: docs/plans/2026-06-14-001-feat-flashcard-deck-plan.md (U3)
 */

/**
 * Returns the active deck — all of the current user's thoughts where
 * in_deck = true. Body-only fields (id + body); the review shuffles
 * client-side, so no ordering is applied.
 */
export const getDeck = async () => {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError || !user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('thoughts')
    .select('id, body')
    .eq('user_id', user.id)
    .eq('in_deck', true);

  if (error) throw error;
  return data || [];
};

/**
 * Toss a card — sets in_deck = false. Sticky: the trigger never re-adds a
 * tossed thought. Does not touch the thought itself.
 */
export const tossCard = async (thoughtId) => {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError || !user) throw new Error('Not authenticated');

  // .select() so we can confirm a row actually changed. A zero-row update
  // (stale id, or RLS blocked it) returns error:null with an empty array —
  // without this check the caller would treat a no-op as success and the
  // optimistic UI would never revert, leaving DB and UI permanently disagreeing.
  const { data, error } = await supabase
    .from('thoughts')
    .update({ in_deck: false })
    .eq('id', thoughtId)
    .eq('user_id', user.id)
    .select('id');

  if (error) throw error;
  if (!data || data.length === 0) throw new Error('Toss did not update any row (not found or not owned)');
  return true;
};

/**
 * Add a thought to the deck — sets in_deck = true. Works for ANY thought
 * regardless of importance, and is also the un-toss path (false -> true).
 */
export const addToDeck = async (thoughtId) => {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError || !user) throw new Error('Not authenticated');

  // .select() to confirm a row changed — see tossCard for why a zero-row
  // update must throw rather than silently succeed.
  const { data, error } = await supabase
    .from('thoughts')
    .update({ in_deck: true })
    .eq('id', thoughtId)
    .eq('user_id', user.id)
    .select('id');

  if (error) throw error;
  if (!data || data.length === 0) throw new Error('Add to deck did not update any row (not found or not owned)');
  return true;
};
